/**
 * Shared filtering of a chart's saved viewer-time contract against the dataset's
 * current date guardrails. Used by both the per-widget viewer controls
 * (ChartWidget) and the tab-level time controls so they expose an identical,
 * in-policy option set. Options present in the saved config but no longer
 * allowed by live guardrails ("compatibility-only" states, PRD-37) are dropped.
 */
import type {
  DateGuardrails,
  TimeSeriesViewerOptions,
  PointInTimeViewerOptions,
  TimeSeriesUnit,
  PointInTimeUnit,
  ViewerSelectableTimePeriod,
} from "@models/Dataset";

export const PIT_PERIOD_TO_UNIT: Record<
  ViewerSelectableTimePeriod,
  PointInTimeUnit
> = {
  latest_month: "month",
  previous_month: "month",
  latest_quarter: "quarter",
  previous_quarter: "quarter",
  latest_semester: "semester",
  previous_semester: "semester",
};

/** Reduce a Time-Series (line) viewer contract to what the guardrails currently allow. */
export function filterTimeSeriesViewerOptions(
  ts: TimeSeriesViewerOptions,
  savedTimeUnit: string,
  dateGuardrails: DateGuardrails,
): TimeSeriesViewerOptions {
  const tsGuardrails = dateGuardrails.timeSeries.units;

  // Filter additional RAs against the chart's saved unit allowed list.
  const savedUnitAllowed =
    tsGuardrails[savedTimeUnit as TimeSeriesUnit]?.allowedRollingAverages ?? [];
  const filteredChartUnitRAs = (
    ts.chartUnitAdditionalRollingAverages ?? []
  ).filter((ra) => savedUnitAllowed.includes(ra));

  // Filter additional units to those still enabled, and intersect their RA
  // lists with the dataset's current allowed list for that unit.
  const filteredAdditionalUnits = Object.fromEntries(
    Object.entries(ts.additionalUnits ?? {}).flatMap(([unit, cfg]) => {
      const g = tsGuardrails[unit as TimeSeriesUnit];
      if (!g?.enabled || !cfg) return [];
      const allowed = g.allowedRollingAverages;
      const filteredRAs = cfg.allowedRollingAverages.filter((ra) =>
        allowed.includes(ra),
      );
      if (filteredRAs.length === 0) return [];
      const defaultRA = filteredRAs.includes(cfg.defaultRollingAverage)
        ? cfg.defaultRollingAverage
        : filteredRAs[0];
      return [
        [unit, { allowedRollingAverages: filteredRAs, defaultRollingAverage: defaultRA }],
      ];
    }),
  ) as TimeSeriesViewerOptions["additionalUnits"];

  return {
    ...ts,
    chartUnitAdditionalRollingAverages: filteredChartUnitRAs,
    additionalUnits: filteredAdditionalUnits,
  };
}

/** Reduce a Point-in-Time (bar/table/crosstab) viewer contract to what the guardrails currently allow. */
export function filterPointInTimeViewerOptions(
  pit: PointInTimeViewerOptions,
  savedTimePeriod: string,
  dateGuardrails: DateGuardrails,
): PointInTimeViewerOptions {
  const pitGuardrails = dateGuardrails.pointInTime.units;

  // Filter additional RAs against the chart's saved period unit allowed list.
  const savedUnit =
    savedTimePeriod in PIT_PERIOD_TO_UNIT
      ? PIT_PERIOD_TO_UNIT[savedTimePeriod as ViewerSelectableTimePeriod]
      : null;
  const savedUnitAllowed = savedUnit
    ? (pitGuardrails[savedUnit]?.allowedRollingAverages ?? [])
    : [];
  const filteredChartPeriodRAs = (
    pit.chartPeriodAdditionalRollingAverages ?? []
  ).filter((ra) => savedUnitAllowed.includes(ra));

  // Filter additional periods to those whose unit is still enabled, and
  // intersect their RA lists with the dataset's current allowed list.
  const filteredAdditionalPeriods = Object.fromEntries(
    Object.entries(pit.additionalPeriods ?? {}).flatMap(([period, cfg]) => {
      const unit = PIT_PERIOD_TO_UNIT[period as ViewerSelectableTimePeriod];
      const g = unit ? pitGuardrails[unit] : null;
      if (!g?.enabled || !cfg) return [];
      const allowed = g.allowedRollingAverages;
      const filteredRAs = cfg.allowedRollingAverages.filter((ra) =>
        allowed.includes(ra),
      );
      if (filteredRAs.length === 0) return [];
      const defaultRA = filteredRAs.includes(cfg.defaultRollingAverage)
        ? cfg.defaultRollingAverage
        : filteredRAs[0];
      return [
        [period, { allowedRollingAverages: filteredRAs, defaultRollingAverage: defaultRA }],
      ];
    }),
  ) as PointInTimeViewerOptions["additionalPeriods"];

  return {
    ...pit,
    chartPeriodAdditionalRollingAverages: filteredChartPeriodRAs,
    additionalPeriods: filteredAdditionalPeriods,
  };
}
